/* eslint-disable react/prop-types */
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import styles from './SearchFilterPills.module.css';

/* ---------------- DEFAULT CONFIG ---------------- */

export const formatRange = (min, max, { suffix = '', format = v => v } = {}) => {
  const hasMin = min != null && min !== '' && !Number.isNaN(Number(min));
  const hasMax = max != null && max !== '' && !Number.isNaN(Number(max));

  const nMin = Number(min);
  const nMax = Number(max);

  if (!hasMin && !hasMax) return null;

  if (hasMin && hasMax) {
    if (nMin === 0 && nMax === 0) return null;
    return `${format(nMin)} - ${format(nMax)}${suffix}`;
  }

  if (hasMin) {
    return `${format(nMin)}+${suffix}`;
  }

  return `0 - ${format(nMax)}${suffix}`;
};

const DEFAULT_RANGE_FORMATTERS = intl => ({
  price: (min, max) =>
    formatRange(min, max, {
      suffix: '€',
      format: v => v.toLocaleString(),
    }),

  pub_length: (min, max) => formatRange(min, max, { suffix: 'm' }),

  beam: (min, max) => formatRange(min, max, { suffix: 'm' }),

  draught: (min, max) => formatRange(min, max, { suffix: 'm' }),

  pub_clearance: (min, max) => formatRange(min, max, { suffix: 'm' }),

  pub_passengerCapacity: (min, max) =>
    formatRange(min, max, {
      suffix: ` ${intl.formatMessage({ id: 'General.people' })}`,
    }),

  year: (min, max) => formatRange(min, max),
});

const DEFAULT_IGNORED_KEYS = ['page', 'sort', 'limit', 'status'];

const LABEL_MAP = {
  category: 'category',
  type: 'type',
  pub_hullMaterial: 'material',
  fuelType: 'fuelType',
};

/**
 * Measures which pills fit within `maxRows` rows of the container.
 * Returns the count of visible pills (those fitting in the allowed rows).
 */
function useVisibleCount(containerRef, pills, maxRows) {
  const [visibleCount, setVisibleCount] = useState(pills.length);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || pills.length === 0) {
      setVisibleCount(pills.length);
      return;
    }

    function measure() {
      const children = Array.from(container.children);
      // Filter only pill elements (skip the "+X" button if present)
      const pillElements = children.filter(el => el.classList.contains(styles.pill));

      if (pillElements.length === 0) {
        setVisibleCount(0);
        return;
      }

      let rowCount = 1;
      let prevTop = pillElements[0].offsetTop;
      let cutoff = pillElements.length;

      for (let i = 1; i < pillElements.length; i++) {
        const top = pillElements[i].offsetTop;
        if (top > prevTop) {
          rowCount++;
          prevTop = top;
        }
        if (rowCount > maxRows) {
          cutoff = i;
          break;
        }
      }

      setVisibleCount(cutoff);
    }

    // Measure after paint so the DOM is laid out
    const frameId = requestAnimationFrame(measure);

    // Re-measure on resize
    const observer = new ResizeObserver(measure);
    observer.observe(container);

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [containerRef, pills, maxRows]);

  return visibleCount;
}

/* ---------------- PARSER ---------------- */

function parsePills(searchParams, { rangeFormatters, ignoredKeys }, intl) {
  const pills = [];

  const mergedFormatters = { ...DEFAULT_RANGE_FORMATTERS(intl), ...rangeFormatters };
  const mergedIgnored = new Set([...DEFAULT_IGNORED_KEYS, ...(ignoredKeys || [])]);

  const entries = Array.from(searchParams.entries());

  // A category with type pills is narrowed to those types (see
  // util/listingTaxonomy.js), so a pill for the whole category would describe
  // a filter that is not applied. Only older links carry both.
  const narrowedCategories = new Set(
    searchParams
      .getAll('type')
      .map(v => decodeURIComponent(v).split('/'))
      .filter(segments => segments.length === 2)
      .map(([category]) => category)
  );

  for (const [key, rawValue] of entries) {
    const value = decodeURIComponent(rawValue);

    if (mergedIgnored.has(key) || !value.trim()) continue;
    if (key === 'category' && narrowedCategories.has(value)) continue;

    //range formatter (e.g. price=1000,5000)
    if (mergedFormatters[key]) {
      const parts = value.split(',');
      if (parts.length === 2) {
        const label = mergedFormatters[key](parts[0], parts[1]);
        if (label) {
          pills.push({
            key,
            value: rawValue,
            label,
            id: `${key}::${rawValue}`,
          });
        }
      }
      continue;
    }

    if (key === 'type' && value.includes('/')) {
      const segments = value.split('/');
      // Type labels are keyed per category: 'yacht' reads differently under
      // powerBoats and sailingBoats.
      const label = intl.formatMessage({
        id: `General.listing.type.${segments[0]}.${segments[1]}`,
      });
      pills.push({
        key,
        value: rawValue,
        label,
        id: `${key}::${rawValue}`,
      });
      continue;
    }

    const label = LABEL_MAP[key]
      ? intl.formatMessage({ id: `General.listing.${LABEL_MAP[key]}.${value}` }) || value
      : value;
    pills.push({
      key,
      value: rawValue,
      label,
      id: `${key}::${rawValue}`,
    });
  }

  return pills;
}

/* ---------------- COMPONENT ---------------- */

export default function SearchFilterPills({
  labelMap = {},
  rangeFormatters = {},
  ignoredKeys = [],
  onFilterRemove,
  maxRows = 2,
  resetAllButton,
}) {
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();

  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);

  // Create URLSearchParams from current URL
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const pills = useMemo(
    () =>
      parsePills(
        searchParams,
        {
          labelMap,
          rangeFormatters,
          ignoredKeys,
        },
        intl
      ),
    [searchParams, labelMap, rangeFormatters, ignoredKeys]
  );

  const visibleCount = useVisibleCount(containerRef, pills, maxRows);
  const hiddenCount = pills.length - visibleCount;
  const hasOverflow = hiddenCount > 0 && !expanded;
  const displayedPills = hasOverflow ? pills.slice(0, visibleCount) : pills;

  // Reset expanded state when pills change (e.g. filter removed)
  useEffect(() => {
    setExpanded(false);
  }, [pills.length]);

  const removePill = useCallback(
    pill => {
      const params = new URLSearchParams(location.search);

      const allValues = params.getAll(pill.key);

      if (allValues.length > 1) {
        params.delete(pill.key);
        allValues.filter(v => v !== pill.value).forEach(v => params.append(pill.key, v));
      } else {
        params.delete(pill.key);
      }

      if (onFilterRemove) {
        onFilterRemove(pill.key, pill.value);
      }

      history.push({
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      });
    },
    [location, history, onFilterRemove]
  );

  if (pills.length === 0) return null;

  return (
    <div className={styles.wrapper}>
      {/*
        Hidden measuring container — renders ALL pills off-screen so we can
        measure how many fit in maxRows without the user seeing layout shifts.
      */}
      <ul
        ref={containerRef}
        className={`${styles.pillsContainer} ${styles.measuring}`}
        aria-hidden="true"
      >
        {pills.map(pill => (
          <li key={pill.id} className={styles.pill}>
            <span className={styles.pillLabel}>{pill.label}</span>
            <span className={styles.removeButton}>&times;</span>
          </li>
        ))}
      </ul>

      {/* Visible container */}
      <ul className={styles.pillsContainer} role="list" aria-label="Active filters">
        {displayedPills.map(pill => (
          <li key={pill.id} className={styles.pill}>
            <span className={styles.pillLabel}>{pill.label}</span>
            <button
              type="button"
              className={styles.removeButton}
              onClick={() => removePill(pill)}
              aria-label={`Remove filter: ${pill.label}`}
            >
              &times;
            </button>
          </li>
        ))}

        {hasOverflow && (
          <li className={styles.pill}>
            <button
              type="button"
              className={styles.showMoreButton}
              onClick={() => setExpanded(true)}
              aria-label={`Show ${hiddenCount} more filters`}
            >
              +{hiddenCount} filters
            </button>
          </li>
        )}
        {displayedPills?.length && resetAllButton}
      </ul>
    </div>
  );
}
